/**
 * Custom Lexical Image Node for the markdown editor.
 * Supports image display with alt text, dimensions, and markdown serialization.
 *
 * ## DOM Element Caching Strategy (Prevents Image Flash)
 *
 * When Lexical's editor state is rebuilt (e.g., during track changes diff or
 * external content sync), all nodes are destroyed and recreated. This normally
 * causes React to unmount and remount components, which makes images "flash"
 * as the browser re-renders them.
 *
 * ### The Solution: DOM Element Reuse
 *
 * We use a three-tier caching strategy:
 *
 * 1. **dimensionsCache** (Map<stableCacheKey, {width, height}>)
 *    - Stores image dimensions by URL pathname (without query params/tokens)
 *    - Allows immediate aspect-ratio calculation on remount
 *    - Persists across component lifecycle
 *
 * 2. **imageCache** (Map<fullUrl, HTMLImageElement>)
 *    - Stores preloaded Image objects by full URL
 *    - Used for dimension lookup and browser cache warming
 *
 * 3. **domElementCache** (Map<stableCacheKey, HTMLImageElement>) ⭐ KEY INNOVATION
 *    - Stores the ACTUAL rendered <img> DOM elements
 *    - When component remounts, we appendChild() the SAME element
 *    - Browser doesn't re-render - the element just moves in the DOM tree
 *    - Result: ZERO flash, even during full editor rebuilds
 *
 * ### Why This Works
 *
 * Instead of React controlling the <img> element (which gets destroyed on unmount),
 * we render a <span> container and manually manage the <img> inside it via DOM APIs.
 * The <img> element lives in our cache and survives React's reconciliation.
 *
 * ### Cache Key Strategy
 *
 * Firebase Storage URLs include tokens that change: `?token=abc123`
 * We normalize URLs to just the pathname for cache keys, so the same image
 * with different tokens shares the cached DOM element.
 *
 * @see getStableCacheKey - Extracts pathname from URL for stable caching
 */

import type {
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';
import { $applyNodeReplacement, DecoratorNode } from 'lexical';
import { ImageComponent } from './ImageComponent';
import type { ReactNode } from 'react';

export interface ImagePayload {
  src: string;
  altText?: string;
  width?: number;
  height?: number;
  key?: NodeKey;
}

export type SerializedImageNode = Spread<
  {
    src: string;
    altText: string;
    width?: number;
    height?: number;
  },
  SerializedLexicalNode
>;

function convertImageElement(domNode: Node): null | DOMConversionOutput {
  if (domNode instanceof HTMLImageElement) {
    const { src, alt, width, height } = domNode;
    const node = $createImageNode({
      src,
      altText: alt,
      width: width || undefined,
      height: height || undefined,
    });
    return { node };
  }
  return null;
}

export class ImageNode extends DecoratorNode<ReactNode> {
  __src: string;
  __altText: string;
  __width?: number;
  __height?: number;

  static getType(): string {
    return 'image';
  }

  static clone(node: ImageNode): ImageNode {
    return new ImageNode(
      node.__src,
      node.__altText,
      node.__width,
      node.__height,
      node.__key,
    );
  }

  static importJSON(serializedNode: SerializedImageNode): ImageNode {
    const { src, altText, width, height } = serializedNode;
    return $createImageNode({
      src,
      altText,
      width,
      height,
    });
  }

  static importDOM(): DOMConversionMap | null {
    return {
      img: () => ({
        conversion: convertImageElement,
        priority: 0,
      }),
    };
  }

  constructor(
    src: string,
    altText?: string,
    width?: number,
    height?: number,
    key?: NodeKey,
  ) {
    super(key);
    this.__src = src;
    this.__altText = altText || '';
    this.__width = width;
    this.__height = height;
  }

  exportJSON(): SerializedImageNode {
    return {
      type: 'image',
      version: 1,
      src: this.__src,
      altText: this.__altText,
      width: this.__width,
      height: this.__height,
    };
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('img');
    element.setAttribute('src', this.__src);
    element.setAttribute('alt', this.__altText);
    if (this.__width) {
      element.setAttribute('width', String(this.__width));
    }
    return { element };
  }

  createDOM(config: EditorConfig): HTMLElement {
    const span = document.createElement('span');
    const theme = config.theme;
    const className = theme.image;
    if (className !== undefined) {
      span.className = className;
    }
    span.style.display = 'inline-block';
    return span;
  }

  updateDOM(): false {
    return false;
  }

  getSrc(): string {
    return this.__src;
  }

  getAltText(): string {
    return this.__altText;
  }

  decorate(): ReactNode {
    return (
      <ImageComponent
        src={this.__src}
        alt={this.__altText}
        width={this.__width}
        height={this.__height}
      />
    );
  }
}

export function $createImageNode({
  src,
  altText,
  width,
  height,
  key,
}: ImagePayload): ImageNode {
  return $applyNodeReplacement(new ImageNode(src, altText, width, height, key));
}

export function $isImageNode(
  node: LexicalNode | null | undefined,
): node is ImageNode {
  return node instanceof ImageNode;
}
